import { autoinject } from 'aurelia-dependency-injection';
import { bindable, bindingMode } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import numeral from 'numeral';
import { AccountPaymentItem } from '../../models/account-payment-item';
import { PaymentItemInitialEnteredAmount, PaymentItemType, TransactionBalancePaymentType } from '../../models/enums';
import { PaymentItemTableConfiguration } from '../../models/payment-item-table-configuration';
import { ServiceResponse } from '../../models/service-response';
import { PaymentItemService } from './payment-item-service';
import { PaymentItemTableSettings } from './persistence/payment-item-table-settings';
import { ComponentValidationResult } from './validation/component-validation-result';
import { IValidationRule } from './validation/i-validation-rule';
import { RuleValidationResult } from './validation/rule-validation-result';
import { EnteredAmountGreaterThanZeroRule } from './validation/rules/entered-amount-greater-than-zero-rule';
import { MeetsAccountMinAmountRule } from './validation/rules/meets-account-min-amount-rule';
import { MeetsPortalAndPortfolioMaxAmountRule } from './validation/rules/meets-portal-and-portfolio-max-amount-rule';
import { MeetsPortalAndPortfolioMinAmountRule } from './validation/rules/meets-portal-and-portfolio-min-amount-rule';
import { SelectAtLeastOneItemRule } from './validation/rules/select-at-least-one-item-rule';
import { VerifyShortPayReasonCommentRule } from './validation/rules/verify-short-pay-reason-comment-rule';
import { VerifyShortPaymentsAllowedRule } from './validation/rules/verify-short-payments-allowed-rule';
import { VerifyOverPaymentsAllowedRule } from './validation/rules/verify-over-payments-allowed-rule';
import { NumberHelper } from '../../helpers/number-helper';

@autoinject
export class PaymentItemTable {
	@bindable({ defaultBindingMode: bindingMode.twoWay }) configuration: PaymentItemTableConfiguration;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) businessKey: number;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) customerAccountKeys: number[];
	@bindable({ defaultBindingMode: bindingMode.twoWay }) forceRefreshOfData: boolean;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) items: AccountPaymentItem[] = [];
	@bindable({ defaultBindingMode: bindingMode.twoWay }) feeAmountGreaterThanZero: boolean;

	// callbacks
	@bindable({ defaultBindingMode: bindingMode.oneWay }) itemsLoaded;
	@bindable({ defaultBindingMode: bindingMode.oneWay }) selectedItemsChanged;
	@bindable({ defaultBindingMode: bindingMode.oneWay }) enteredAmountChanged;

	// outputs
	@bindable({ defaultBindingMode: bindingMode.toView }) hasMultipleMerchants: boolean;

	// local vars
	showDiscount = false;
	showDueDate = false;
	showBucket: boolean;
	showSubtotal: boolean = false;
	showMinPaymentDue: boolean;
	showCustomerAccountName: boolean;
	showContactName: boolean;
	isPayOffEarly: boolean;
	balanceLabel: string;
	paymentAmountLabel: string;
	portalMinimumPaymentAmount: number;
	portalMaximumPaymentAmount: number;
	selectedItemMerchantKey: number;
	enableMultiSelect: boolean = false;
	subtotalSpacing: number;
	feesEnabled: boolean = false;

	constructor(
		private paymentItemService: PaymentItemService,
		private i18n: I18N
	) {
	}

	attached() {
		// populate from persistent storage, which should've been initialized at some point
		this.configuration = this.paymentItemService.loadConfiguration();
		this.businessKey = this.paymentItemService.loadBusinessKey();
		this.customerAccountKeys = this.paymentItemService.loadCustomerAccountKeys();
		this.forceRefreshOfData = this.paymentItemService.loadForceRefreshOfData();
		this.loadItems();
	}

	configurationChanged(newValue: PaymentItemTableConfiguration, oldValue: PaymentItemTableConfiguration): void {
		if (newValue !== undefined) {
			this.paymentItemService.saveConfiguration(newValue);
		}
	}

	businessKeyChanged(newValue: number, oldValue: number): void {
		if (newValue !== undefined) {
			this.paymentItemService.saveBusinessKey(newValue);
		}
	}

	customerAccountKeysChanged(newValue: number[], oldValue: number[]): void {
		if (newValue !== undefined) {
			this.paymentItemService.saveCustomerAccountKeys(newValue);
		}
	}

	forceRefreshOfDataChanged(newValue: boolean, oldValue: boolean): void {
		if (newValue !== undefined) {
			this.paymentItemService.saveForceRefreshOfData(newValue);
		}
	}

	loadItems(): void {
		// if all required inputs have been specified, make the call to load data
		if (this.configuration && this.configuration.itemType === PaymentItemType.Account &&
			this.businessKey !== undefined &&
			this.customerAccountKeys && this.customerAccountKeys.length > 0 &&
			this.forceRefreshOfData !== undefined
		) {
			this.loadSettings();
		}
	}

	loadSettings(): void {
		this.paymentItemService.getPaymentItemTableSettings(this.businessKey, this.customerAccountKeys, this.forceRefreshOfData)
			.then((response: ServiceResponse) => {
				if (response.isSuccessful) {
					let settings: PaymentItemTableSettings = response.value;
					let merchantKey: number;

					this.enableMultiSelect = this.configuration.useMultiselect
						? settings.isShoppingCartEnabled || false
						: false;
					this.showBucket = this.configuration.showBucket && settings.isBusinessBucketEnabled;
					this.showSubtotal = this.configuration.showSubtotal || false;
					this.showMinPaymentDue = settings.displayMinPaymentDue;
					this.portalMinimumPaymentAmount = settings.portalMinimumPaymentAmount;
					this.portalMaximumPaymentAmount = settings.portalMaximumPaymentAmount;
					this.showCustomerAccountName = settings.displayCustomerAccountName;
					this.showContactName = this.configuration.isGuarantorBillingEnabled;
					this.items = settings.items;
					this.isPayOffEarly = this.configuration.isPayOffEarly || false;
					this.getLabels();

					this.configuration.showShortPayReason = {};

					this.items.forEach((item: AccountPaymentItem) => {
						// if payment amount cannot be edited, it should be set to the full amount
						if (this.configuration.showPaymentAmount) {
							if (this.configuration.isPaymentAmountEditable) {
								item.enteredAmount = null;
							} else if (this.configuration.initialAmountType === PaymentItemInitialEnteredAmount.pendingBalance) {
								item.enteredAmount = item.adjustedBalances.pendingBalance.toFixed(2);
							} else {
								item.enteredAmount = item.initialAmount.toFixed(2);
							}
						}

						// Check for multiple merchants
						if (merchantKey === undefined) {
							merchantKey = item.merchantKey;
						} else if (merchantKey !== item.merchantKey) {
							this.hasMultipleMerchants = true;
						}
					});

					let selectedFirstItem: boolean = false;
					if (this.items && this.items.length > 0 && this.configuration.selectFirstItemUponLoad) {
						this.items[0].isSelected = true;
						selectedFirstItem = true;
					}

					this.paymentItemService.saveItems(this.items);

					this.itemsLoaded({
						paymentItems: this.items
					});

					if (selectedFirstItem) {
						this.selectedItemsChanged({
							selectedPaymentItems: [this.items[0]]
						});
					}

					this.getSelectedItemMerchantKey();
					this.getShowDiscount();
					this.getShowDueDate();
					this.getSubtotalSpacing();
				}

				// else handle error case?
			});
	}

	getLabels() {
		if (this.isPayOffEarly) {
			this.balanceLabel = this.i18n.i18next.t('accountBalance', 'Account Balance');
			this.paymentAmountLabel = this.i18n.i18next.t('remainingPlanAmount', 'Remaining Plan Amount');
		} else if (this.configuration.paymentAmountLabel) {
			this.balanceLabel = this.i18n.i18next.t('balance', 'Balance');
			this.paymentAmountLabel = this.configuration.paymentAmountLabel;
		} else {
			this.balanceLabel = this.i18n.i18next.t('balance', 'Balance');
			this.paymentAmountLabel = this.i18n.i18next.t('paymentAmount', 'Payment Amount');
		}
	}

	/**
	 * Handle enter/spacebar keys and prevent double event fires
	 */
	rowPressed(event: KeyboardEvent, selectedItem: AccountPaymentItem): void {
		event.stopPropagation();
		event.preventDefault();
		if (event.keyCode === 13 || event.keyCode === 32) {
			this.rowClicked(selectedItem);
		}
	}

	rowClicked(selectedItem: AccountPaymentItem): void {
		if (this.configuration.enableItemSelection) {
			this.items.forEach((item: AccountPaymentItem) => {
				let disabled = this.isItemDisabled(item);
				if (item.itemKey === selectedItem.itemKey) {
					item.isSelected = disabled ? item.isSelected : !item.isSelected; // Only flip the selected flag if item is not disabled
				} else if (!this.enableMultiSelect) {
					item.isSelected = false;
				}
			});

			let selectedItems = this.items.filter(item => item.isSelected);
			this.selectedItemsChanged({
				selectedPaymentItems: selectedItems
			});

			this.getSelectedItemMerchantKey();
			this.paymentItemService.saveItems(this.items);
		}
	}

	enteredAmountKeyup(item: AccountPaymentItem) {
		this.paymentItemService.saveItems(this.items);
		this.enteredAmountChanged({
			changedPaymentItem: [ item ]
		});
	}

	filterEnteredAmountInput(event: KeyboardEvent) {
		let validInput = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '.'];
		let input = event.char || event.key; // IE11 is a terrible browser that reads the numpad '.' key as 'Del' this forces IE11 to use the char value of '.'
		return (validInput.indexOf(input) !== -1);
	}

	formatEnteredAmount(item: AccountPaymentItem) {
		// Remove dollar and comma characters.
		// This is in case the user pastes the amount.
		if (item.enteredAmount) {
			item.enteredAmount = item.enteredAmount.replace(/[$,]/g, '');
		}

		item.enteredAmount = numeral(item.enteredAmount).format('(0.00)');
		this.renderShortPayReason(item);

		// Trigger enteredAmountChanged
		this.enteredAmountChanged({
			changedPaymentItem: [item]
		});
	}

	renderShortPayReason(item: AccountPaymentItem) {
		let paymentAmount = parseFloat(item.enteredAmount) || 0;
		let accountBalance = item.initialAmount;

		let renderIt: boolean = paymentAmount < accountBalance
			&& item.paymentLimitSettings.allowShortPay
			&& item.paymentLimitSettings.requireShortPayReason;

		this.configuration.showShortPayReason[item.paymentItemId] = renderIt;
	}

	getTotalEnteredAmountForSelectedItems(): number {
		let total: number = 0;

		this.items.forEach((i: AccountPaymentItem) => {
			// try to get entered amount for each selected item. Ignore those with invalid amounts.
			if (i.isSelected) {
				// Remove dollar and comma characters.
				// This is in case the user pastes the amount.
				if (i.enteredAmount) {
					i.enteredAmount = i.enteredAmount.replace(/[$,]/g, '');
				}

				// Make sure the amount is a valid floating-point number.
				let parsedAmount: number = parseFloat(i.enteredAmount);
				if (!isNaN(parsedAmount)) {
					total += NumberHelper.parseCurrency(parsedAmount.toFixed(2));
				}
			}
		});

		return total;
	}

	getTotalDiscountAmountForSelectedItems(): number {
		let total: number = 0;

		this.items.forEach((i: AccountPaymentItem) => {
			// try to get entered amount for each selected item. Ignore those with invalid amounts.
			if (i.isSelected) {
				// Make sure the amount is a valid floating-point number.
				total += NumberHelper.parseCurrency((i.initialAmount * i.discount / 100).toFixed(2));
			}
		});

		return total;
	}

	// Show discount if any item has one set, and configured for payInFull
	getShowDiscount() {
		this.items.forEach((item: AccountPaymentItem) => {
			if (
                item.isSelected
                && item.discount
                && item.discount > 0
                && this.configuration.isPayInFull
            ) {
				this.showDiscount = true;
				item.enteredAmount = (item.initialAmount * (1 - item.discount / 100)).toFixed(2);
				this.enteredAmountChanged({
					changedPaymentItem: [ item ]
				});
			}
		});
	}

	// only show due date if configured for it and at least one visible account has a value for it
	getShowDueDate() {
		this.showDueDate = false;
		if (this.configuration.showDueDate) {
			this.items.forEach((item: AccountPaymentItem) => {
				if (!this.showDueDate) {
					// if only showing selected, check selected items. Otherwise, check across all items.
					if (this.configuration.onlyShowSelected) {
						if (item.isSelected && item.dueDate) {
							this.showDueDate = true;
						}
					} else if (item.dueDate) {
						this.showDueDate = true;
					}
				}
			});
		}
	}

	validate(): ComponentValidationResult {
		// do the validation checks in multiple waves to prevent duplicate or redundant error
		// messages. In the future it would be nice to chain these in a smarter way.
		let componentResult: ComponentValidationResult;

		this.items.forEach((i: AccountPaymentItem) => {
			i.paymentBalanceType = TransactionBalancePaymentType.NotSet;
		});

		// first wave of validation rules
		let rules: IValidationRule[] = [];
		rules.push(new SelectAtLeastOneItemRule(this.i18n, this.items));
		rules.push(new MeetsPortalAndPortfolioMinAmountRule(this.i18n, this.items, this.portalMinimumPaymentAmount));
		rules.push(new MeetsPortalAndPortfolioMaxAmountRule(this.i18n, this.items, this.portalMaximumPaymentAmount));
		rules.push(new MeetsAccountMinAmountRule(this.i18n, this.items));

		rules.push(new VerifyShortPaymentsAllowedRule(this.i18n, this.items, this.configuration));
		rules.push(new VerifyShortPayReasonCommentRule(this.i18n, this.items, this.configuration));
		rules.push(new VerifyOverPaymentsAllowedRule(this.i18n, this.items));

		componentResult = this.validateRules(rules);
		if (!componentResult.isValid) {
			return componentResult;
		}

		// next wave
		rules = [];
		rules.push(new EnteredAmountGreaterThanZeroRule(this.i18n, this.items));
		componentResult = this.validateRules(rules);
		if (!componentResult.isValid) {
			return componentResult;
		}

		return componentResult;
	}

	getSelectedItemMerchantKey() {
		let firstSelected = this.items.find(item => item.isSelected);
		if (firstSelected) {
			this.selectedItemMerchantKey = firstSelected.merchantKey;
			this.feesEnabled = firstSelected.areFeesEnabled;
		} else {
			this.selectedItemMerchantKey = null;
		}

		this.updateItemDisabledState();
	}

	updateItemDisabledState() {
		this.items.forEach(item => {
			item.disabled = this.isItemDisabled(item);
		});
	}

	private validateRules(rules: IValidationRule[]): ComponentValidationResult {
		let result: ComponentValidationResult = new ComponentValidationResult();
		rules.forEach((r: IValidationRule) => {
			let ruleResult: RuleValidationResult = r.validate();
			if (!ruleResult.isValid) {
				result.isValid = false;
				result.errorMessages.push(ruleResult.errorMessage);
				result.confirm = ruleResult.confirm || false;
				if (result.confirm) {
					result.confirmErrorMessageIndex = result.errorMessages.length - 1;
				}
			}
		});
		return result;
	}

	getSelectedItems(): AccountPaymentItem[] {
		return this.items.filter((i: AccountPaymentItem) => i.isSelected);
	}

	isItemDisabled(item: AccountPaymentItem): boolean {
		// When shopping cart is enabled and the provided item is a different merchant return true
		let disabled = false;

		if (
			this.enableMultiSelect
			&& this.selectedItemMerchantKey
			&& item.merchantKey !== this.selectedItemMerchantKey
		) {
			disabled = true;
		}

		return disabled;
	}

	get subtotal() {
		let selected: AccountPaymentItem[] = this.items.filter(item => item.isSelected);
		let total: number = selected.reduce((accumulator: number, item: AccountPaymentItem): number => {
			return accumulator + parseFloat(item.enteredAmount) || 0;
		}, 0);

		return total;
	}

	getSubtotalSpacing() {
		let cols = 1; // number of non if/show bound columns

		if (this.configuration.enableItemSelection) {
			cols++;
		}
		if (this.showCustomerAccountName) {
			cols++;
		}
		if (this.configuration.showInitialAmount) {
			cols++;
		}
		if (this.showMinPaymentDue) {
			cols++;
		}
		if (this.showDueDate) {
			cols++;
		}
		if (this.showDiscount) {
			cols++;
		}

		this.subtotalSpacing = cols;
	}
}
